import { useEffect, useState } from 'react';
import type { ControlKey } from '../../content/types';
import { bangLevels } from '../../simulation/controller';
import type { ControllerKind, ExperimentConfig, FeedforwardMode } from '../../simulation/types';
import { useApp } from '../../state/store';
import { Term } from '../Term';
import { NumberField } from './NumberField';
import { useSim } from './useSim';

const KIND_LABELS: Record<ControllerKind, string> = {
  manual: 'Manual',
  'open-loop': 'Open loop (feedforward only)',
  'bang-bang': 'Bang-bang',
  p: 'P',
  pi: 'PI',
  pid: 'PID',
};

const FF_LABELS: Record<FeedforwardMode, string> = {
  off: 'Off (T_ff = 0)',
  'operating-point': 'Operating point (T_ff = T₀)',
  'target-equilibrium': 'Target equilibrium',
};

interface Props {
  config: ExperimentConfig;
  controls: Set<ControlKey | 'all'>;
  kinds: ControllerKind[];
}

export function ControlsPanel({ config, controls, kinds }: Props) {
  const update = useApp((s) => s.updateConfig);
  const setManual = useApp((s) => s.setManual);
  const sim = useSim();
  const has = (k: ControlKey) => controls.has('all') || controls.has(k);
  const c = config.controller;
  const all = controls.has('all');
  const allowedKinds = Array.from(new Set([...kinds, c.kind]));

  // Live manual value mirrors the running simulation, not the stored config.
  const [manualLive, setManualLive] = useState(c.manualInput);
  useEffect(() => setManualLive(c.manualInput), [config, c.manualInput]);
  useEffect(() => {
    const last = sim.manualEvents[sim.manualEvents.length - 1];
    setManualLive(last ? last.value : c.manualInput);
  }, [sim.manualEvents, c.manualInput]);

  const set = (fn: (c: ExperimentConfig) => void) => update(fn);
  const levels = c.kind === 'bang-bang' ? bangLevels(c, config.plant, config.z0, config.reference.initial) : null;

  return (
    <div className="controls">
      {(has('controller-kind') || has('feedforward')) && (
        <fieldset>
          <legend>Controller</legend>
          {has('controller-kind') && allowedKinds.length > 1 && (
            <label className="field">
              <span className="field-label">
                <Term id="controller">Controller type</Term>
              </span>
              <select
                value={c.kind}
                onChange={(e) =>
                  set((x) => {
                    x.controller.kind = e.target.value as ControllerKind;
                  })
                }
              >
                {allowedKinds.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!(has('controller-kind') && allowedKinds.length > 1) && (
            <p className="small">
              Controller: <strong>{KIND_LABELS[c.kind]}</strong>
            </p>
          )}
          {has('feedforward') && (
            <label className="field">
              <span className="field-label">
                <Term id="feedforward">Feedforward</Term>
              </span>
              <select
                value={c.ffMode}
                onChange={(e) =>
                  set((x) => {
                    x.controller.ffMode = e.target.value as FeedforwardMode;
                  })
                }
              >
                {(Object.keys(FF_LABELS) as FeedforwardMode[]).map((m) => (
                  <option key={m} value={m}>
                    {FF_LABELS[m]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </fieldset>
      )}

      {c.kind === 'manual' && (
        <fieldset>
          <legend>{c.ffMode === 'off' ? 'Manual thrust' : 'Manual correction'}</legend>
          <NumberField
            name={c.ffMode === 'off' ? 'Manual thrust T' : 'Manual correction u_fb'}
            label={c.ffMode === 'off' ? <Term id="T">Thrust request</Term> : <Term id="ufb">Correction u_fb</Term>}
            unit="N"
            value={manualLive}
            min={c.ffMode === 'off' ? 0 : -8}
            max={c.ffMode === 'off' ? config.plant.Tmax : 8}
            step={0.01}
            onLive={(v) => {
              setManualLive(v);
              setManual(v);
            }}
            onCommit={(v) => {
              setManualLive(v);
              setManual(v);
            }}
            hint="Changes apply live during a run and are recorded for replay."
          />
          <div className="row gap-s">
            {(c.ffMode === 'off' ? [8.5, 9.81, 11] : [-1, 0, 1]).map((v) => (
              <button
                key={v}
                type="button"
                className="btn small ghost"
                onClick={() => {
                  setManualLive(v);
                  setManual(v);
                }}
              >
                {v} N
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {(c.kind === 'p' || c.kind === 'pi' || c.kind === 'pid') && (has('Kp') || has('Ki') || has('Kd')) && (
        <fieldset>
          <legend>Gains</legend>
          {has('Kp') && (
            <NumberField
              name="K_p"
              label={<Term id="Kp" />}
              unit="N/m"
              value={c.Kp}
              min={0}
              max={80}
              step={0.5}
              onCommit={(v) => set((x) => void (x.controller.Kp = v))}
            />
          )}
          {(c.kind === 'pi' || c.kind === 'pid') && has('Ki') && (
            <NumberField
              name="K_i"
              label={<Term id="Ki" />}
              unit="N/(m·s)"
              value={c.Ki}
              min={0}
              max={40}
              step={0.5}
              onCommit={(v) => set((x) => void (x.controller.Ki = v))}
            />
          )}
          {c.kind === 'pid' && has('Kd') && (
            <NumberField
              name="K_d"
              label={<Term id="Kd" />}
              unit="N·s/m"
              value={c.Kd}
              min={0}
              max={30}
              step={0.5}
              onCommit={(v) => set((x) => void (x.controller.Kd = v))}
            />
          )}
          {(c.kind === 'pi' || c.kind === 'pid') && has('anti-windup') && (
            <label className="check">
              <input type="checkbox" checked={c.antiWindup} onChange={(e) => set((x) => void (x.controller.antiWindup = e.target.checked))} />{' '}
              <Term id="anti-windup">Anti-windup</Term>
            </label>
          )}
          {c.kind === 'pid' && has('filter') && (
            <NumberField
              name="Derivative filter time constant tau"
              label={
                <>
                  <Term id="filter">Derivative filter τ</Term>
                </>
              }
              unit="s"
              value={c.derivTau}
              min={0}
              max={0.3}
              step={0.01}
              onCommit={(v) => set((x) => void (x.controller.derivTau = v))}
            />
          )}
        </fieldset>
      )}

      {c.kind === 'bang-bang' && (has('bang') || all) && (
        <fieldset>
          <legend>Bang-bang</legend>
          <NumberField name="Amplitude Delta" label="Amplitude Δ" unit="N" value={c.bangDelta} min={0} max={15} step={0.5} onCommit={(v) => set((x) => void (x.controller.bangDelta = v))} />
          <NumberField name="Hysteresis half-band h" label={<Term id="h">Half-band h</Term>} unit="m" value={c.hysteresis} min={0} max={0.3} step={0.01} onCommit={(v) => set((x) => void (x.controller.hysteresis = v))} />
          <label className="field">
            <span className="field-label">Initial switch state</span>
            <select value={c.bangInitial} onChange={(e) => set((x) => void (x.controller.bangInitial = e.target.value as 'high' | 'low'))}>
              <option value="low">low</option>
              <option value="high">high</option>
            </select>
          </label>
          {levels && (
            <p className="small">
              Applied levels after limits: high <strong>{levels.high.toFixed(2)} N</strong>, low <strong>{levels.low.toFixed(2)} N</strong>
              {(levels.lowRequested < 0 || levels.highRequested > config.plant.Tmax) && (
                <span className="warn-text"> (a requested level is outside 0…{config.plant.Tmax} N and is clamped)</span>
              )}
            </p>
          )}
        </fieldset>
      )}

      {(has('scenario') || all) && (
        <fieldset>
          <legend>Scenario</legend>
          <NumberField name="Initial target r" label={<Term id="reference">Initial target r</Term>} unit="m" value={config.reference.initial} min={0.5} max={10} step={0.1} onCommit={(v) => set((x) => void (x.reference.initial = v))} />
          <StepEditor
            label="Target step"
            unit="m"
            step={config.reference.steps[0]}
            defaultValue={config.reference.initial + 0.5}
            min={0.5}
            max={10}
            onChange={(s) => set((x) => void (x.reference.steps = s ? [s] : []))}
          />
          <StepEditor
            label="Disturbance step d"
            unit="N"
            step={config.disturbance.steps[0]}
            defaultValue={-1}
            min={-10}
            max={10}
            onChange={(s) => set((x) => void (x.disturbance.steps = s ? [s] : []))}
          />
          <NumberField name="Initial altitude" label="Initial altitude" unit="m" value={config.initial.z} min={0.5} max={10} step={0.05} onCommit={(v) => set((x) => void (x.initial.z = v))} />
          <NumberField name="Duration" label="Duration" unit="s" value={config.duration} min={2} max={60} step={1} onCommit={(v) => set((x) => void (x.duration = v))} />
        </fieldset>
      )}

      {has('noise') && (
        <fieldset>
          <legend>Sensor</legend>
          <label className="check">
            <input type="checkbox" checked={config.noise.enabled} onChange={(e) => set((x) => void (x.noise.enabled = e.target.checked))} />{' '}
            <Term id="n">Sensor noise</Term>
          </label>
          {config.noise.enabled && (
            <>
              <NumberField name="Noise standard deviation" label="Noise σ" unit="m" value={config.noise.sigma} min={0} max={0.05} step={0.001} onCommit={(v) => set((x) => void (x.noise.sigma = v))} />
              <NumberField name="Noise seed" label="Seed" value={config.noise.seed} min={1} max={999} step={1} onCommit={(v) => set((x) => void (x.noise.seed = Math.round(v)))} />
            </>
          )}
        </fieldset>
      )}

      {(has('plant') || all) && (
        <fieldset>
          <legend>Vehicle (plant)</legend>
          <NumberField name="Damping b" label={<Term id="b">Damping b</Term>} unit="N·s/m" value={config.plant.b} min={0} max={12} step={0.5} onCommit={(v) => set((x) => void (x.plant.b = v))} />
          <NumberField name="Spring stiffness k" label={<Term id="k">Spring k</Term>} unit="N/m" value={config.plant.k} min={0} max={10} step={0.5} onCommit={(v) => set((x) => void (x.plant.k = v))} />
          {all && (
            <>
              <NumberField name="Mass m" label={<Term id="m">Mass m</Term>} unit="kg" value={config.plant.m} min={0.2} max={3} step={0.05} onCommit={(v) => set((x) => void (x.plant.m = v))} />
              <NumberField name="Maximum thrust" label={<Term id="Tmax">Max thrust</Term>} unit="N" value={config.plant.Tmax} min={5} max={60} step={1} onCommit={(v) => set((x) => void (x.plant.Tmax = v))} />
            </>
          )}
        </fieldset>
      )}

      {(has('estimated') || all) && c.ffMode !== 'off' && (
        <fieldset>
          <legend>Model mismatch (feedforward estimates)</legend>
          <label className="check">
            <input
              type="checkbox"
              checked={c.estimated !== null}
              onChange={(e) =>
                set((x) => {
                  x.controller.estimated = e.target.checked ? { m: x.plant.m, g: x.plant.g, k: x.plant.k, zs: x.plant.zs } : null;
                })
              }
            />{' '}
            Feedforward uses its own estimates
          </label>
          {c.estimated && (
            <>
              <NumberField name="Estimated mass" label="Estimated mass" unit="kg" value={c.estimated.m} min={0.2} max={3} step={0.05} onCommit={(v) => set((x) => void (x.controller.estimated!.m = v))} />
              <NumberField name="Estimated spring stiffness" label="Estimated k" unit="N/m" value={c.estimated.k} min={0} max={10} step={0.5} onCommit={(v) => set((x) => void (x.controller.estimated!.k = v))} />
              <p className="hint">The vehicle keeps its true values; only the prediction changes.</p>
            </>
          )}
        </fieldset>
      )}
    </div>
  );
}

function StepEditor({
  label,
  unit,
  step,
  defaultValue,
  min,
  max,
  onChange,
}: {
  label: string;
  unit: string;
  step: { t: number; value: number } | undefined;
  defaultValue: number;
  min: number;
  max: number;
  onChange: (s: { t: number; value: number } | null) => void;
}) {
  return (
    <div className="step-editor">
      <label className="check">
        <input type="checkbox" checked={!!step} onChange={(e) => onChange(e.target.checked ? { t: 1, value: defaultValue } : null)} /> {label}
      </label>
      {step && (
        <div className="grid-2">
          <NumberField name={`${label} time`} label="at time" unit="s" value={step.t} min={0} max={30} step={0.5} onCommit={(v) => onChange({ ...step, t: v })} />
          <NumberField name={`${label} value`} label="value" unit={unit} value={step.value} min={min} max={max} step={0.1} onCommit={(v) => onChange({ ...step, value: v })} />
        </div>
      )}
    </div>
  );
}
